import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { DocumentWithVersionInfo } from "@/types";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DocumentList from "@/components/documents/document-list";

interface ProjectDocumentsProps {
  projectId: string;
  loading?: boolean;
}

export default function ProjectDocuments({ projectId, loading = false }: ProjectDocumentsProps) {
  const [activeTab, setActiveTab] = useState<string>("all");
  
  // Fetch documents for this project
  const { data: documents, isLoading: isLoadingDocuments, error, refetch } = useQuery<DocumentWithVersionInfo[]>({
    queryKey: [`/api/projects/${projectId}/documents`],
    enabled: !!projectId && !loading,
  });
  
  const filteredDocuments = documents?.filter(doc => {
    if (activeTab === "all") return true;
    // Map database documentType to UI tabs (case-insensitive)
    const uiType = doc.documentType?.toLowerCase() === "contract" ? "contract" : "other";
    return uiType === activeTab;
  }) || [];
  
  // Show loading state when component loading or documents are loading
  if (loading || isLoadingDocuments) {
    return (
      <div className="mt-6 space-y-6">
        <div className="bg-muted animate-pulse rounded-md h-10 w-48 mb-4" />
        <div className="bg-muted animate-pulse rounded-md h-12 w-full" />
        <div className="bg-muted animate-pulse rounded-md h-64 w-full" />
      </div>
    );
  }
  
  // Show error state if document loading failed
  if (error) {
    return (
      <div className="mt-6 space-y-6">
        <h2 className="text-xl font-semibold text-neutral-800">Project Documents</h2>
        <div className="p-6 text-center bg-red-50 rounded-lg border border-red-100">
          <p className="text-red-600">Failed to load documents. Please try again later.</p>
          <Button variant="outline" className="mt-4" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="project-documents">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-neutral-800">Project Documents</h2>
      </div>
      
      {/* Tab navigation for document types */}
      <Tabs defaultValue="all" className="w-full mb-6" onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full md:w-1/2 lg:w-1/3">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="contract">Contract</TabsTrigger>
          <TabsTrigger value="other">Other</TabsTrigger>
        </TabsList>
      </Tabs>
      
      {/* Document count */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-neutral-600">
          {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}
        </p>
      </div>
      
      {/* Document List */}
      <DocumentList 
        documents={filteredDocuments.map(doc => ({
          ...doc,
          // Ensure required fields are present for DocumentList compatibility
          documentType: doc.documentType || "project",
          versionNumber: doc.versionNumber || 1,
        }))} 
        entityType="contract" 
        entityId={projectId}
      />
    </div>
  );
}